import { useEffect, useState, useRef } from "react";
import { createApplicationHubConnection } from "../infrastructure/signalr-service";
import { ApplicationListItem } from "../dtos/ApplicantListItem";
import { InterviewDetails } from "../dtos/ApplicationDetails";
import { BulkRejectionProgressDto, BulkVideoProgressDto, BulkAiCallProgressDto, BulkAiCallCandidateProgressDto } from "../applicants-service";

export function useApplicationSignalR(
  organizationId: string | undefined,
  vacancyId: string | undefined,
  onNewApplication: (application: ApplicationListItem) => void,
  onApplicationUpdated?: (application: ApplicationListItem) => void,
  onInterviewUpdated?: (interview: InterviewDetails) => void,
  onBulkRejectionProgress?: (progress: BulkRejectionProgressDto) => void,
  onBulkRejectionComplete?: () => void,
  onBulkVideoProgress?: (progress: BulkVideoProgressDto) => void,
  onBulkVideoComplete?: () => void,
  onBulkAiCallProgress?: (progress: BulkAiCallProgressDto) => void,
  onBulkAiCallComplete?: () => void,
  onBulkAiCallCandidateProgress?: (progress: BulkAiCallCandidateProgressDto) => void,
) {
  const [isConnected, setIsConnected] = useState(false);
  const onNewApplicationRef = useRef(onNewApplication);
  const onApplicationUpdatedRef = useRef(onApplicationUpdated);
  const onInterviewUpdatedRef = useRef(onInterviewUpdated);
  const onBulkRejectionProgressRef = useRef(onBulkRejectionProgress);
  const onBulkRejectionCompleteRef = useRef(onBulkRejectionComplete);
  const onBulkVideoProgressRef = useRef(onBulkVideoProgress);
  const onBulkVideoCompleteRef = useRef(onBulkVideoComplete);
  const onBulkAiCallProgressRef = useRef(onBulkAiCallProgress);
  const onBulkAiCallCompleteRef = useRef(onBulkAiCallComplete);
  const onBulkAiCallCandidateProgressRef = useRef(onBulkAiCallCandidateProgress);

  useEffect(() => {
    onNewApplicationRef.current = onNewApplication;
    onApplicationUpdatedRef.current = onApplicationUpdated;
    onInterviewUpdatedRef.current = onInterviewUpdated;
    onBulkRejectionProgressRef.current = onBulkRejectionProgress;
    onBulkRejectionCompleteRef.current = onBulkRejectionComplete;
    onBulkVideoProgressRef.current = onBulkVideoProgress;
    onBulkVideoCompleteRef.current = onBulkVideoComplete;
    onBulkAiCallProgressRef.current = onBulkAiCallProgress;
    onBulkAiCallCompleteRef.current = onBulkAiCallComplete;
    onBulkAiCallCandidateProgressRef.current = onBulkAiCallCandidateProgress;
  }, [onNewApplication, onApplicationUpdated, onInterviewUpdated, onBulkRejectionProgress, onBulkRejectionComplete, onBulkVideoProgress, onBulkVideoComplete, onBulkAiCallProgress, onBulkAiCallComplete, onBulkAiCallCandidateProgress]);

  useEffect(() => {
    if (!organizationId || !vacancyId) {
      return;
    }

    const { connection, start, stop } = createApplicationHubConnection(
      organizationId
    );

    connection.on("NewApplication", (applicationDto: ApplicationListItem) => {
      if (applicationDto.vacancyId === vacancyId) {
        onNewApplicationRef.current(applicationDto);
      }
    });

    connection.on("ApplicationUpdated", (applicationDto: ApplicationListItem) => {
      if (applicationDto.vacancyId === vacancyId && onApplicationUpdatedRef.current) {
        onApplicationUpdatedRef.current(applicationDto);
      }
    });

    connection.on("InterviewUpdated", (interviewDto: InterviewDetails) => {
      if (onInterviewUpdatedRef.current) {
        onInterviewUpdatedRef.current(interviewDto);
      }
    });

    connection.on("BulkRejectionProgress", (progress: BulkRejectionProgressDto) => {
      onBulkRejectionProgressRef.current?.(progress);
      if (progress.status === "Completed" || progress.status === "Failed") {
        onBulkRejectionCompleteRef.current?.();
      }
    });

    connection.on("BulkVideoProgress", (progress: BulkVideoProgressDto) => {
      onBulkVideoProgressRef.current?.(progress);
      if (progress.status === "Completed" || progress.status === "Failed") {
        onBulkVideoCompleteRef.current?.();
      }
    });

    connection.on("BulkAiCallProgress", (progress: BulkAiCallProgressDto) => {
      onBulkAiCallProgressRef.current?.(progress);
      if (progress.status === "Completed" || progress.status === "Failed") {
        onBulkAiCallCompleteRef.current?.();
      }
    });

    connection.on("BulkAiCallCandidateProgress", (progress: BulkAiCallCandidateProgressDto) => {
      onBulkAiCallCandidateProgressRef.current?.(progress);
    });

    connection.onreconnecting(() => {
      console.log("SignalR Reconnecting...");
      setIsConnected(false);
    });

    connection.onreconnected(() => {
      console.log("SignalR Reconnected");
      setIsConnected(true);
    });

    connection.onclose(() => {
      console.log("SignalR Connection Closed");
      setIsConnected(false);
    });

    start().then(() => setIsConnected(true));

    return () => {
      stop();
    };
  }, [organizationId, vacancyId]);

  return { isConnected };
}
